// Downloads that work in the browser and in the desktop app. WKWebView (macOS) does not save link downloads, so
// the desktop app fetches the bytes and the Rust side writes them into a folder (src-tauri/src/lib.rs save_file).
import { invoke } from '@tauri-apps/api/core';
import { isDesktop } from '../platform';

/** Fired after the desktop app saved a file: { path } (the main window shows a toast with „Öffnen“). */
export const SAVED_EVENT = 'rimon:saved';

/** Folder for ZIP exports (Präferenzen → Import → Ordner); the rest goes to Downloads. Empty = Downloads too.
 *  The folder is created when the first ZIP is saved there (save_file), not before. */
let zipDir = '~/Rimon/Compta/Vorbereitung';
export const setZipDir = (dir: string | undefined) => { if (dir !== undefined) zipDir = dir.trim() || '~/Downloads'; };
export const ZIP_DIR = () => zipDir;

const safe = (name: string) => name.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'download';

// User rule 15.10.2026: a file is never opened by itself and never as a page of its own (no tab, no navigation to a
// file URL, no system app). Two ways only: a download (browser: a file with its name; desktop: „Speichern unter“) or
// RimoPreview (pdf.js). Printing runs from a hidden frame inside the app.

/** Saves bytes. Browser: a download with its name. Desktop: „Speichern unter“ prefilled with `dir` + name; returns the
 *  chosen path (null when cancelled or in the browser). */
export async function saveBytes(bytes: Uint8Array, filename: string, opts: { dir?: string; mime?: string } = {}): Promise<string | null> {
  if (!isDesktop()) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([bytes as BlobPart], { type: opts.mime ?? 'application/octet-stream' }));
    a.download = safe(filename); a.rel = 'noopener'; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
    return null;
  }
  const path = await invoke<string>('save_file', bytes, { headers: { 'x-dir': encodeURIComponent(opts.dir ?? '~/Downloads'), 'x-name': encodeURIComponent(safe(filename)) } });
  if (!path) return null; // cancelled
  window.dispatchEvent(new CustomEvent(SAVED_EVENT, { detail: { path } }));
  return path;
}

/** Fetches a file (signed URL) and saves it like saveBytes – never by navigating to the URL. */
export async function saveUrl(url: string, filename: string, opts: { dir?: string } = {}): Promise<string | null> {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Download fehlgeschlagen (' + res.status + ')');
  return saveBytes(new Uint8Array(await res.arrayBuffer()), filename, { ...opts, mime: res.headers.get('content-type') ?? undefined });
}

/** „Im Finder zeigen“ for a saved file (desktop only). */
export const revealPath = (path: string) => invoke('reveal_path', { path });

/** Prints a file from a hidden frame inside the app (blob, same origin): the print dialog, no tab, no system app. */
export async function printBytes(bytes: Uint8Array, mime = 'application/pdf'): Promise<void> {
  const src = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mime })), f = document.createElement('iframe');
  f.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none';
  f.setAttribute('aria-hidden', 'true');
  f.src = src;
  f.onload = () => setTimeout(() => {
    try { f.contentWindow?.focus(); f.contentWindow?.print(); } catch { /* nothing to print */ }
    setTimeout(() => { f.remove(); URL.revokeObjectURL(src); }, 60_000);
  }, 300);
  document.body.appendChild(f);
}

/** Prints a file behind a (signed) URL: fetched first, then printBytes. `filename` is kept for the callers. */
export async function printUrl(url: string, _filename?: string): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Druck fehlgeschlagen (' + res.status + ')');
  await printBytes(new Uint8Array(await res.arrayBuffer()), res.headers.get('content-type') ?? 'application/pdf');
}
